import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Button } from "./ui/button";
import {
  TrendingUp, TrendingDown, Download, Star, Route, Award,
} from "lucide-react";
import { CountUp } from "./motion/MotionPrimitives";
import { TiltCard3D } from "./motion/MotionPremium";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["#1F4032", "#3A6B57", "#5A917A", "#7CB89E", "#A8D4BE", "#D4EBDE"];
const STATUS_LABELS = {
  open: "Ouverte", accepted: "Acceptée", loaded: "Chargée",
  en_route: "En route", delivered: "Livrée", cancelled: "Annulée",
};

function StatCard({ label, value, sub, icon: Icon, color = "text-[#1F4032]", numeric, decimals = 0, prefix = "", suffix = "" }) {
  return (
    <TiltCard3D intensity={8} className="bg-white p-5 border border-[#1F4032]/10">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-muted-foreground">{label}</div>
          <div className="font-display font-black text-3xl mt-1 tracking-tight">
            {numeric != null
              ? <CountUp value={numeric} decimals={decimals} prefix={prefix} suffix={suffix} />
              : value}
          </div>
          {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
        </div>
        {Icon && <Icon className={`w-5 h-5 ${color} shrink-0 mt-1`} strokeWidth={1.5} />}
      </div>
    </TiltCard3D>
  );
}

export default function TransporterAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/transporter/analytics")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = async () => {
    try {
      const resp = await api.get("/transporter/missions.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "missions-mml.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_) {
      // Fallback : URL directe (cookie auth)
      window.open(`${API_URL}/api/transporter/missions.csv`, "_blank");
    }
  };

  if (loading) return null;
  if (!data) return null;

  const hasAnyActivity = data.delivered_count > 0 || data.rating_count > 0
    || Object.values(data.by_status).some((v) => v > 0);
  if (!hasAnyActivity) return null;

  const statusPie = Object.entries(data.by_status)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, key: k }));

  const ratingBars = Object.entries(data.rating_distribution)
    .map(([k, v]) => ({ stars: `${k}★`, count: v }))
    .reverse(); // 5★ d'abord

  const isPositive = data.delta_pct > 0;
  const isNeg = data.delta_pct < 0;

  return (
    <div className="mt-8 space-y-6" data-testid="transporter-analytics">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-accent mb-1">— Statistiques</div>
          <h2 className="font-display font-black text-3xl tracking-tighter">Mon activité</h2>
        </div>
        <Button onClick={downloadCsv} variant="outline" className="rounded-full gap-2 border-[#1F4032] hover:bg-[#1F4032] hover:text-white" data-testid="export-csv-btn">
          <Download className="w-4 h-4" /> Exporter en CSV
        </Button>
      </div>

      {/* KPI top row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F4032]/10 border border-[#1F4032]/10">
        <StatCard
          label="Revenu ce mois-ci"
          numeric={data.current_month_revenue}
          decimals={2}
          suffix=" €"
          icon={isPositive ? TrendingUp : isNeg ? TrendingDown : null}
          color={isPositive ? "text-emerald-600" : isNeg ? "text-red-600" : ""}
          sub={data.delta_pct ? `${data.delta_pct > 0 ? "+" : ""}${data.delta_pct}% vs mois dernier` : "Pas de revenu le mois précédent"}
        />
        <StatCard
          label="Missions livrées"
          numeric={data.delivered_count}
          sub={`${data.completion_rate}% de réussite`}
          icon={Award}
        />
        <StatCard
          label="Distance parcourue"
          numeric={data.total_distance_km}
          suffix=" km"
          decimals={0}
          sub={`= ${(data.total_distance_km / 40075 * 100).toFixed(2)}% du tour de la Terre`}
          icon={Route}
        />
        <StatCard
          label="Note moyenne"
          value={data.rating_count > 0 ? `${data.rating_average.toFixed(1)}★` : "—"}
          sub={data.rating_count > 0 ? `Sur ${data.rating_count} avis` : "Aucun avis encore"}
          icon={Star}
          color="text-amber-500"
        />
      </div>

      {/* Revenue line chart */}
      <div className="bg-white border border-[#1F4032]/10 p-5">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-4">
          <h3 className="font-display font-bold text-lg tracking-tight">Évolution des revenus (12 derniers mois)</h3>
          <div className="text-xs font-mono text-neutral-500 shrink-0">Net transporteur en €</div>
        </div>
        <div style={{ width: "100%", minHeight: 240, height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={data.monthly} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#737373" fontSize={11} />
              <YAxis stroke="#737373" fontSize={11} tickFormatter={(v) => `${v}€`} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                formatter={(v) => [`${Number(v).toFixed(2)}€`, "Revenus"]}
              />
              <Line type="monotone" dataKey="earnings" stroke="#1F4032" strokeWidth={2.5}
                dot={{ r: 4, fill: "#1F4032" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row : status pie + missions bar */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#1F4032]/10 p-5">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Mes missions par statut</h3>
          {statusPie.length === 0 ? (
            <div className="text-sm text-neutral-500 text-center py-12">Aucune mission</div>
          ) : (
            <div style={{ width: "100%", minHeight: 220, height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusPie}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                  >
                    {statusPie.map((entry, idx) => (
                      <Cell key={entry.name || `cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(value, entry) => `${value} : ${entry?.payload?.value ?? ""}`}
                  />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#1F4032]/10 p-5">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Volume mensuel (missions livrées)</h3>
          <div style={{ width: "100%", minHeight: 220, height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={data.monthly} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#737373" fontSize={11} />
                <YAxis stroke="#737373" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="missions" fill="#3A6B57" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Ratings distribution */}
      {data.rating_count > 0 && (
        <div className="bg-white border border-[#1F4032]/10 p-5">
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-4">
            <h3 className="font-display font-bold text-lg tracking-tight">Distribution de mes notes</h3>
            <div className="text-sm">
              <span className="text-2xl font-bold text-amber-500">{data.rating_average.toFixed(1)}</span>
              <span className="text-neutral-500 ml-1">/ 5 · {data.rating_count} avis</span>
            </div>
          </div>
          <div style={{ width: "100%", minHeight: 180, height: 180 }}>
            <ResponsiveContainer>
              <BarChart data={ratingBars} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" stroke="#737373" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="stars" stroke="#737373" fontSize={12} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="count" fill="#F59E0B" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
